import { useState, useEffect, useRef } from "react";

const DURATION_MS = 800;

/**
 * Parses value for count-up: number (int/float) or string like "LKR 760,000" or "32%".
 * Returns { numericPart, prefix, suffix, isCurrency } so we can animate the number and recombine.
 */
function parseDisplayValue(value) {
  if (value == null) return { numericPart: 0, prefix: "", suffix: "", isCurrency: false };
  const str = String(value).trim();
  const currencyMatch = str.match(/^([^\d]*)([\d.,]+)(.*)$/);
  if (currencyMatch) {
    const [, prefix, num, suffix] = currencyMatch;
    const numericPart = parseFloat(num.replace(/,/g, "")) || 0;
    return { numericPart, prefix: prefix.trim(), suffix: (suffix || "").trim(), isCurrency: /^(LKR|USD|\$|€|£)/i.test(prefix.trim()) };
  }
  const num = parseFloat(str);
  if (!Number.isNaN(num)) return { numericPart: num, prefix: "", suffix: str.includes("%") ? "%" : "", isCurrency: false };
  return { numericPart: 0, prefix: "", suffix: "", isCurrency: false };
}

function formatNumber(n, isCurrency, decimals = 0) {
  if (isCurrency) return Math.round(n).toLocaleString();
  return decimals > 0 ? n.toFixed(decimals) : Math.round(n).toLocaleString();
}

/**
 * useCountUp(value, options)
 * Animates from 0 to value over ~800ms when value changes.
 * value can be number or string like "LKR 760,000" or "32%".
 * Returns display string to render.
 */
export function useCountUp(value, options = {}) {
  const { duration = DURATION_MS, decimals } = options;
  const targetStr = value != null ? String(value) : "";
  const [display, setDisplay] = useState("");
  const rafRef = useRef(null);
  const startRef = useRef({ value: null, startTime: null, startN: 0 });

  useEffect(() => {
    const nextParsed = parseDisplayValue(value);
    const targetN = nextParsed.numericPart;
    const prefix = nextParsed.prefix;
    const suffix = nextParsed.suffix;
    const isCurrency = nextParsed.isCurrency;

    const startN = startRef.current.value === value ? startRef.current.startN : 0;
    startRef.current = { value, startN, startTime: null };

    const zeroStr = prefix ? `${prefix} 0${suffix}` : `0${suffix}`;
    setDisplay(zeroStr);

    let startTime = null;
    const tick = (now) => {
      if (startTime == null) startTime = now;
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const currentN = startN + (targetN - startN) * eased;
      const numStr = formatNumber(currentN, isCurrency, decimals);
      const next = prefix ? `${prefix} ${numStr}${suffix}` : `${numStr}${suffix}`;
      setDisplay(next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration, decimals]);

  if (display !== "") return display;
  return value != null ? String(value) : "";
}
